import { PayloadAction } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { AssessmentName, OntMode, UpdateServiceSubTypeInput } from '@mfe/shared/schema-types';

import { CommandState } from '../../../../platform/src/server/dataSources/PSM/schemaTypes';
import { selectHasFiber, selectUser } from '../auth';
import { selectConfig } from '../config';
import {
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutationWithErrors,
  graphqlQueryWithErrors,
} from '../graphql';
import {
  canFetchFiberServiceSubType,
  getFiberServiceSubType,
  getSsidName,
  resetFiberServiceSubType,
  runDiagnostic,
  setDiagnostic,
  setFiberServiceSubType,
  setFiberServiceSubTypeError,
  setHasUpdatedSsidName,
  setHasUpdatedSsidPassword,
  setNetworkError,
  setNewSsidPassword,
  setRunDate,
  setSsidError,
  setSsidName,
  setSsidRateLimitError,
  setSubTypeUpdateStatus,
  setUpdateSSIDSuccess,
  updateServiceSubType,
  updateSsidInfo,
  UpdateSsidInfoAction,
} from './networkSlice';
import {
  GET_DIAGNOSTICS_QUERY,
  GET_FIBER_SERVICE_SUB_TYPE,
  GET_SSID_INFO,
  UPDATE_SERVICE_SUB_TYPE,
  UPDATE_WIFI_INFO,
} from './queries-and-mutations';
import { waitForToken } from '../utils/utilsSagas';

export function* fetchDiagnosticResult() {
  yield call(waitForToken);

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_DIAGNOSTICS_QUERY,
    variables: {
      doRefresh: false,
    },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setNetworkError(runtimeError ?? errors));
    return;
  }

  if (data?.getDiagnostics) {
    const filteredDiagnostic = data.getDiagnostics.filter(
      (diagnostic) => diagnostic.name !== AssessmentName.Billing && diagnostic.name !== AssessmentName.DataUsage
    );

    const runDate = format(new Date(), 'MMMM do h:mm aa');
    yield put(setRunDate(runDate));
    yield put(setDiagnostic(filteredDiagnostic));
  }
}

export function* fetchSsid() {
  try {
    yield call(waitForToken);

    const {
      user: { productInstanceId },
    }: ReturnType<typeof selectUser> = yield select(selectUser);

    if (!productInstanceId) {
      return;
    }

    const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_SSID_INFO,
      variables: { productInstanceId },
      fetchPolicy: 'no-cache',
    });

    const { data, errors, runtimeError } = apiResponse;

    if (runtimeError || errors) {
      yield put(setSsidError(runtimeError ?? errors));
      return;
    }

    if (data?.getSsidInfo?.ssid) {
      yield put(setSsidName(data.getSsidInfo.ssid));
    } else {
      yield put(setSsidName(''));
    }
  } catch (error) {
    yield put(setSsidError(error));
  }
}

export function* handleSsidInfoUpdate(action: UpdateSsidInfoAction) {
  try {
    yield call(waitForToken);

    const { ssid, passphrase } = action.payload;

    if (!ssid && !passphrase) {
      return;
    }

    const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: UPDATE_WIFI_INFO,
      // PSM expects both ssid and passphrase, even if only one of them is being updated
      // PSM will also throw and error if ssid and passphrase provided have not changed
      variables: { input: { ssid, passphrase } },
      fetchPolicy: 'no-cache',
    });

    const { data, errors, runtimeError } = apiResponse;

    if (runtimeError || errors) {
      const errorString = JSON.stringify(runtimeError ?? errors ?? '');
      if (errorString.includes('RATE_LIMIT_EXCEEDED')) {
        yield put(setSsidRateLimitError(true));
      } else {
        yield put(setNetworkError(runtimeError ?? errors));
      }
      return;
    }

    if (data?.updateSsidInfo) {
      yield put(setUpdateSSIDSuccess());
      if (data.updateSsidInfo.newSsid) {
        yield put(setHasUpdatedSsidName(true));
        yield put(setSsidName(data.updateSsidInfo.newSsid));
      }
      if (data.updateSsidInfo.newPassphrase) {
        yield put(setHasUpdatedSsidPassword(true));
        yield put(setNewSsidPassword(data.updateSsidInfo.newPassphrase));
      }
    } else {
      yield put(setNetworkError('Failed to update WiFi info'));
    }
  } catch (error) {
    yield put(setNetworkError(error));
  }
}

export function* fetchFiberServiceSubType() {
  const {
    user: { productInstanceStatus },
  }: ReturnType<typeof selectUser> = yield select(selectUser);
  const { enableFiber, enableBridgeMode }: ReturnType<typeof selectConfig> = yield select(selectConfig);

  try {
    yield call(waitForToken);

    const hasFiber: boolean = yield select(selectHasFiber);
    const canFetchOntMode: boolean = canFetchFiberServiceSubType({
      enableFiber,
      enableBridgeMode,
      hasFiber,
      productInstanceStatus,
    });

    // Only call API when the feature is enabled for active Fiber accounts
    if (!canFetchOntMode) {
      yield put(resetFiberServiceSubType());
      return;
    }

    const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_FIBER_SERVICE_SUB_TYPE,
      fetchPolicy: 'network-only',
    });

    const { data, errors, runtimeError } = apiResponse;

    if (runtimeError || errors) {
      yield put(setFiberServiceSubTypeError(runtimeError ?? errors));
      return;
    }

    if (data?.getFiberServiceSubType?.ontMode) {
      yield put(setFiberServiceSubType(data.getFiberServiceSubType.ontMode));
      return;
    }

    yield put(setFiberServiceSubTypeError('Unable to determine ONT mode'));
  } catch (error) {
    yield put(setFiberServiceSubTypeError(error));
  }
}

export function* handleUpdateServiceSubType(action: PayloadAction<UpdateServiceSubTypeInput>) {
  const {
    user: { productInstanceStatus },
  }: ReturnType<typeof selectUser> = yield select(selectUser);
  const { enableFiber, enableBridgeMode }: ReturnType<typeof selectConfig> = yield select(selectConfig);

  try {
    yield call(waitForToken);

    const hasFiber: boolean = yield select(selectHasFiber);
    const canUpdateOntMode: boolean = canFetchFiberServiceSubType({
      enableFiber,
      enableBridgeMode,
      hasFiber,
      productInstanceStatus,
    });

    // Only call API when the feature is enabled for active Fiber accounts
    if (!canUpdateOntMode) {
      return;
    }

    if (action.payload.ontMode === OntMode.WiFi) {
      if (!action.payload.wifiSsid || !action.payload.wifiPassphrase) {
        throw new Error('Both WiFi SSID and WiFi passphrase are required');
      }
    }

    const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: UPDATE_SERVICE_SUB_TYPE,
      variables: {
        input: action.payload,
      },
      fetchPolicy: 'network-only',
    });

    const { data } = apiResponse;

    if (data?.updateServiceSubType?.error) {
      yield put(setSubTypeUpdateStatus(data.updateServiceSubType));
      yield put(setFiberServiceSubTypeError(data?.updateServiceSubType?.error));
      return;
    }

    if (data?.updateServiceSubType?.status === CommandState.Succeeded) {
      yield put(setFiberServiceSubType(action.payload.ontMode));
      yield put(setSubTypeUpdateStatus(data?.updateServiceSubType));
      if (action.payload?.wifiSsid) {
        yield put(setSsidName(action.payload?.wifiSsid));
      }

      return;
    }

    yield put(setFiberServiceSubTypeError('Unable to determine ONT mode'));
  } catch (error) {
    yield put(setFiberServiceSubTypeError(error));
  }
}

export function* watchNetwork() {
  yield takeLatest(runDiagnostic.type, fetchDiagnosticResult);
  yield takeLatest(getSsidName.type, fetchSsid);
  yield takeLatest(updateSsidInfo.type, handleSsidInfoUpdate);
  yield takeLatest(getFiberServiceSubType.type, fetchFiberServiceSubType);
  yield takeLatest(updateServiceSubType.type, handleUpdateServiceSubType);
}
